import React from 'react';
import {
  fetchBalanceSheet,
  fetchCashFlowStatement,
  fetchIncomeStatement,
  fetchKeyMetrics,
  fetchStockQuote,
} from '../../services/stockApi';

interface VInvestAIScreenProps {
  isDarkMode: boolean;
  selectedStock: string;
}

type ValuationMethod = {
  name: string;
  perShare: number | null;
  notes?: string;
};

type ValuationResult = {
  symbol: string;
  companyName: string | null;
  price: number | null;
  marketCap: number | null;
  sharesOutstanding: number | null;
  sharesOutstandingSource: 'quote' | 'incomeStatement' | 'marketCap/price' | 'missing';

  baseFCF: number | null;
  baseOwnerEarnings: number | null;
  fcfCurrency: string | null;
  growthRate: number;
  discountRate: number;
  terminalGrowthRate: number;
  projectionYears: number;

  cash: number | null;
  totalDebt: number | null;
  netCash: number | null;

  enterpriseValue: number | null;
  equityValue: number | null;
  intrinsicValuePerShare: number | null; // DCF(FCF)

  methods: ValuationMethod[];
  compositeIntrinsicValuePerShare: number | null;
  marginOfSafety: number | null; // vs composite
  buyBelow: number | null; // 30% MOS vs composite
  sellAbove: number | null; // 20% premium vs composite

  // Quality & sanity checks
  fcfPositiveYears5: number | null;
  revenueCagr5y: number | null;
  netMarginLatest: number | null;
  roicLatest: number | null;
  roeLatest: number | null;
  currentRatioLatest: number | null;
  debtToFCF: number | null;
  fcfYield: number | null;

  targetYears: number;
  targetAnnualReturn: number;
  entryPriceForTargetReturn: number | null;
  impliedAnnualReturnToIntrinsic: number | null;
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

const formatCompactUSD = (value: number | null) => {
  if (value === null || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(2)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const formatUSD = (value: number | null, decimals: number = 2) => {
  if (value === null || !Number.isFinite(value)) return '—';
  return `$${value.toFixed(decimals)}`;
};

const formatPercent = (value: number | null, decimals: number = 1) => {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(decimals)}%`;
};

const getLatestByDate = <T extends { date: string }>(items: T[]): T | null => {
  if (!Array.isArray(items) || items.length === 0) return null;
  const sorted = [...items].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  return sorted[0] ?? null;
};

const median = (values: number[]) => {
  const xs = values.filter(v => Number.isFinite(v)).sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 === 0 ? (xs[mid - 1] + xs[mid]) / 2 : xs[mid];
};

const toNumberOrNull = (v: any): number | null => (Number.isFinite(v) ? Number(v) : null);

const computeOwnerEarnings = (cash: {
  operatingCashFlow: number;
  capitalExpenditure: number;
  stockBasedCompensation: number;
}): number | null => {
  const ocf = toNumberOrNull(cash.operatingCashFlow);
  const capex = toNumberOrNull(cash.capitalExpenditure);
  const sbc = toNumberOrNull(cash.stockBasedCompensation);
  if (ocf === null || capex === null) return null;

  // Conservative owner earnings proxy:
  // OE ≈ Operating Cash Flow − CapEx − Stock-Based Compensation (treat SBC as a real owner cost).
  const oe = ocf - Math.abs(capex) - Math.max(0, sbc ?? 0);
  return Number.isFinite(oe) ? oe : null;
};

const computeRevenueCagr = (incomeStatements: { date: string; revenue: number }[]) => {
  const annual = incomeStatements
    .filter(s => Number.isFinite(s.revenue) && s.revenue > 0)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (annual.length < 2) return null;

  const last = annual[annual.length - 1];
  const first = annual[Math.max(0, annual.length - 6)]; // up to ~5Y window

  const years = Math.max(1, (new Date(last.date).getTime() - new Date(first.date).getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  if (!Number.isFinite(years) || years <= 0) return null;

  const cagr = Math.pow(last.revenue / first.revenue, 1 / years) - 1;
  return Number.isFinite(cagr) ? cagr : null;
};

const discountCashFlows = (base: number, g: number, r: number, n: number) => {
  let pv = 0;
  for (let t = 1; t <= n; t += 1) {
    const cf = base * Math.pow(1 + g, t);
    pv += cf / Math.pow(1 + r, t);
  }
  return pv;
};

const computeIntrinsicValuePerShare = (args: {
  baseFCF: number;
  growthRate: number;
  discountRate: number;
  terminalGrowthRate: number;
  projectionYears: number;
  netCash: number;
  sharesOutstanding: number;
}) => {
  const { baseFCF, growthRate, discountRate, terminalGrowthRate, projectionYears, netCash, sharesOutstanding } = args;

  const pvFCF = discountCashFlows(baseFCF, growthRate, discountRate, projectionYears);
  const fcfN = baseFCF * Math.pow(1 + growthRate, projectionYears);

  // Gordon Growth terminal value
  const denom = discountRate - terminalGrowthRate;
  const terminalValue = denom > 0 ? (fcfN * (1 + terminalGrowthRate)) / denom : 0;
  const pvTerminal = terminalValue / Math.pow(1 + discountRate, projectionYears);

  const enterpriseValue = pvFCF + pvTerminal;
  const equityValue = enterpriseValue + netCash;
  const intrinsicPerShare = sharesOutstanding > 0 ? equityValue / sharesOutstanding : NaN;

  return {
    enterpriseValue,
    equityValue,
    intrinsicPerShare: Number.isFinite(intrinsicPerShare) ? intrinsicPerShare : null,
  };
};

const computeGrahamNumber = (eps: number | null, bookValuePerShare: number | null) => {
  if (eps === null || bookValuePerShare === null) return null;
  if (!Number.isFinite(eps) || !Number.isFinite(bookValuePerShare)) return null;
  if (eps <= 0 || bookValuePerShare <= 0) return null;
  const g = Math.sqrt(22.5 * eps * bookValuePerShare);
  return Number.isFinite(g) ? g : null;
};

const VInvestAIScreen: React.FC<VInvestAIScreenProps> = ({ isDarkMode, selectedStock }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<ValuationResult | null>(null);

  const pageBg = isDarkMode ? '#111827' : '#ffffff';
  const cardBg = isDarkMode ? '#1f2937' : '#ffffff';
  const border = isDarkMode ? '#374151' : '#e5e7eb';
  const text = isDarkMode ? '#e5e7eb' : '#111827';
  const muted = isDarkMode ? '#9ca3af' : '#6b7280';

  const runAnalysis = React.useCallback(async (rawSymbol: string) => {
    const s = rawSymbol.trim().toUpperCase();
    if (!s) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const [quote, income, cashflow, balance, keyMetrics] = await Promise.all([
        fetchStockQuote(s),
        fetchIncomeStatement(s, 'annual', 10),
        fetchCashFlowStatement(s, 'annual', 10),
        fetchBalanceSheet(s, 'annual', 5),
        fetchKeyMetrics(s, 'annual', 5),
      ]);

      const latestIncome = getLatestByDate(income);
      const latestCash = getLatestByDate(cashflow);
      const latestBalance = getLatestByDate(balance);
      const latestKM = getLatestByDate(keyMetrics);

      const price = quote?.price ?? null;
      const companyName = quote?.name ?? null;
      const sharesOutstandingFromQuote = quote?.sharesOutstanding ?? null;
      const marketCap = quote?.marketCap ?? (latestKM?.marketCap ?? null);

      // Shares outstanding fallback chain:
      // 1) quote.sharesOutstanding
      // 2) latest income statement weighted average shares (diluted preferred)
      // 3) approximate from market cap / price (when both are available)
      let sharesOutstanding: number | null = Number.isFinite(sharesOutstandingFromQuote as any)
        ? Number(sharesOutstandingFromQuote)
        : null;
      let sharesOutstandingSource: ValuationResult['sharesOutstandingSource'] = sharesOutstanding !== null ? 'quote' : 'missing';

      if (sharesOutstanding === null) {
        const incomeShares = toNumberOrNull(latestIncome?.weightedAverageShsOutDil ?? latestIncome?.weightedAverageShsOut);
        if (incomeShares !== null && incomeShares > 0) {
          sharesOutstanding = incomeShares;
          sharesOutstandingSource = 'incomeStatement';
        }
      }

      if (sharesOutstanding === null) {
        const mc = toNumberOrNull(marketCap);
        const px = toNumberOrNull(price);
        if (mc !== null && mc > 0 && px !== null && px > 0) {
          sharesOutstanding = mc / px;
          sharesOutstandingSource = 'marketCap/price';
        }
      }

      // Use a conservative FCF baseline: median of last up to 3 positive annual FCF values.
      const fcfCandidates = cashflow
        .filter(r => Number.isFinite(r.freeCashFlow) && r.freeCashFlow !== 0)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 3)
        .map(r => r.freeCashFlow);

      let baseFCF = median(fcfCandidates);
      if (baseFCF === null && latestCash) {
        const ocf = latestCash.operatingCashFlow;
        const capex = latestCash.capitalExpenditure;
        if (Number.isFinite(ocf) && Number.isFinite(capex)) {
          baseFCF = ocf - Math.abs(capex);
        }
      }

      const baseFCFValid = baseFCF !== null && Number.isFinite(baseFCF) && baseFCF > 0;

      const ownerEarningsCandidates = cashflow
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 3)
        .map(r => computeOwnerEarnings(r))
        .filter((v): v is number => v !== null && Number.isFinite(v) && v !== 0);
      const baseOwnerEarnings = median(ownerEarningsCandidates);

      // Buffett-style: keep growth conservative, anchored to revenue CAGR if available.
      const revenueCagr = computeRevenueCagr(income);
      const growthRate = clamp((revenueCagr ?? 0.06) * 0.75, 0.02, 0.10);

      const discountRate = 0.10;
      const terminalGrowthRate = 0.03;
      const projectionYears = 10;

      const cash = latestBalance?.cashAndCashEquivalents ?? null;
      const totalDebt = latestBalance?.totalDebt ?? null;
      const netCash = (cash !== null && totalDebt !== null) ? (cash - totalDebt) : 0;

      const sharesValid = sharesOutstanding !== null && Number.isFinite(sharesOutstanding) && sharesOutstanding > 0;

      const dcfFcf = (sharesValid && baseFCFValid)
        ? computeIntrinsicValuePerShare({
            baseFCF: baseFCF as number,
            growthRate,
            discountRate,
            terminalGrowthRate,
            projectionYears,
            netCash,
            sharesOutstanding: sharesOutstanding as number,
          })
        : { enterpriseValue: NaN, equityValue: NaN, intrinsicPerShare: null };

      const enterpriseValue = dcfFcf.enterpriseValue;
      const equityValue = dcfFcf.equityValue;
      const intrinsicPerShare = dcfFcf.intrinsicPerShare;

      const methods: ValuationMethod[] = [];

      // 1) DCF on Free Cash Flow
      methods.push({
        name: 'DCF (FCF) — intrinsic / share',
        perShare: intrinsicPerShare,
        notes: baseFCFValid ? 'FCF median (≤3y) with conservative growth & discount.' : 'Unavailable (FCF missing or non-positive).',
      });

      // 2) DCF on Owner Earnings (Buffett proxy)
      let ownerEarningsIntrinsic: number | null = null;
      if (sharesValid && baseOwnerEarnings !== null && baseOwnerEarnings > 0) {
        const ownerGrowth = clamp(growthRate * 0.85, 0.01, 0.08);
        const oe = computeIntrinsicValuePerShare({
          baseFCF: baseOwnerEarnings,
          growthRate: ownerGrowth,
          discountRate,
          terminalGrowthRate,
          projectionYears,
          netCash,
          sharesOutstanding: sharesOutstanding as number,
        });
        ownerEarningsIntrinsic = oe.intrinsicPerShare;
      }
      methods.push({
        name: 'DCF (Owner Earnings) — intrinsic / share',
        perShare: ownerEarningsIntrinsic,
        notes: 'Owner earnings ≈ OCF − CapEx − SBC (conservative).',
      });

      // 3) EPV (Earnings Power Value): perpetuity with no growth
      let epvPerShare: number | null = null;
      if (sharesValid && baseOwnerEarnings !== null && baseOwnerEarnings > 0) {
        const epvEnterprise = baseOwnerEarnings / discountRate;
        const epvEquity = epvEnterprise + netCash;
        epvPerShare = epvEquity / (sharesOutstanding as number);
        if (!Number.isFinite(epvPerShare)) epvPerShare = null;
      }
      methods.push({
        name: 'EPV (Owner Earnings, 0% growth) — / share',
        perShare: epvPerShare,
        notes: 'Useful as a downside anchor when growth is uncertain.',
      });

      // 4) Graham Number
      const eps = toNumberOrNull(latestIncome?.epsDiluted ?? latestIncome?.eps ?? quote?.eps);
      const bvps = toNumberOrNull(
        latestKM?.bookValuePerShare ??
        (latestBalance?.totalStockholdersEquity && sharesValid ? latestBalance.totalStockholdersEquity / (sharesOutstanding as number) : null)
      );
      const graham = toNumberOrNull(latestKM?.grahamNumber) ?? computeGrahamNumber(eps, bvps);
      methods.push({
        name: 'Graham Number — / share',
        perShare: graham,
        notes: 'Sanity-check based on EPS & book value.',
      });

      const methodValues = methods
        .map(m => m.perShare)
        .filter((v): v is number => v !== null && Number.isFinite(v) && v > 0);
      const compositeIntrinsicValuePerShare = median(methodValues);

      const targetYears = 6;
      const targetAnnualReturn = 0.12;
      const entryPriceForTargetReturn = compositeIntrinsicValuePerShare !== null
        ? (compositeIntrinsicValuePerShare / Math.pow(1 + targetAnnualReturn, targetYears))
        : null;
      const impliedAnnualReturnToIntrinsic = (price !== null && compositeIntrinsicValuePerShare !== null && price > 0)
        ? (Math.pow(compositeIntrinsicValuePerShare / price, 1 / targetYears) - 1)
        : null;

      const marginOfSafety = (price !== null && compositeIntrinsicValuePerShare !== null && compositeIntrinsicValuePerShare > 0)
        ? (1 - price / compositeIntrinsicValuePerShare)
        : null;

      const buyBelow = compositeIntrinsicValuePerShare !== null ? compositeIntrinsicValuePerShare * 0.7 : null;
      const sellAbove = compositeIntrinsicValuePerShare !== null ? compositeIntrinsicValuePerShare * 1.2 : null;

      // Quality checks (latest)
      const fcfLast5 = cashflow
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 5)
        .map(r => toNumberOrNull(r.freeCashFlow));
      const fcfPositiveYears5 = fcfLast5.length ? fcfLast5.filter(v => v !== null && v > 0).length : null;

      const netMarginLatest = (latestIncome && Number.isFinite(latestIncome.netIncome) && Number.isFinite(latestIncome.revenue) && latestIncome.revenue !== 0)
        ? (latestIncome.netIncome / latestIncome.revenue)
        : null;

      const roicLatest = toNumberOrNull(latestKM?.returnOnInvestedCapital ?? latestKM?.roic);
      const roeLatest = toNumberOrNull(latestKM?.roe);
      const currentRatioLatest = toNumberOrNull(latestKM?.currentRatio);
      const debtToFCF = (totalDebt !== null && Number.isFinite(totalDebt) && baseFCF !== null && baseFCF > 0)
        ? (totalDebt / baseFCF)
        : null;
      const fcfYield = (marketCap !== null && Number.isFinite(marketCap) && marketCap > 0 && baseFCF !== null)
        ? (baseFCF / marketCap)
        : null;

      setResult({
        symbol: s,
        companyName,
        price,
        marketCap,
        sharesOutstanding,
        sharesOutstandingSource,
        baseFCF,
        baseOwnerEarnings,
        fcfCurrency: latestIncome?.reportedCurrency ?? null,
        growthRate,
        discountRate,
        terminalGrowthRate,
        projectionYears,
        cash,
        totalDebt,
        netCash,
        enterpriseValue: Number.isFinite(enterpriseValue) ? enterpriseValue : null,
        equityValue: Number.isFinite(equityValue) ? equityValue : null,
        intrinsicValuePerShare: intrinsicPerShare,
        methods,
        compositeIntrinsicValuePerShare,
        marginOfSafety,
        buyBelow,
        sellAbove,
        fcfPositiveYears5,
        revenueCagr5y: revenueCagr,
        netMarginLatest,
        roicLatest,
        roeLatest,
        currentRatioLatest,
        debtToFCF,
        fcfYield,

        targetYears,
        targetAnnualReturn,
        entryPriceForTargetReturn: (entryPriceForTargetReturn !== null && Number.isFinite(entryPriceForTargetReturn)) ? entryPriceForTargetReturn : null,
        impliedAnnualReturnToIntrinsic: (impliedAnnualReturnToIntrinsic !== null && Number.isFinite(impliedAnnualReturnToIntrinsic)) ? impliedAnnualReturnToIntrinsic : null,
      });
    } catch (e: any) {
      setError(e?.message ? String(e.message) : 'Failed to analyze symbol.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    runAnalysis(selectedStock);
  }, [selectedStock, runAnalysis]);

  const containerStyle: React.CSSProperties = {
    minHeight: 'calc(100vh - 220px)',
    backgroundColor: pageBg,
    padding: '1.25rem 2.5rem',
    color: text,
    boxSizing: 'border-box',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: cardBg,
    border: `1px solid ${border}`,
    borderRadius: '0.75rem',
    padding: '1rem',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: 800,
    margin: 0,
    color: text,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '0.95rem',
    fontWeight: 700,
    margin: '0 0 0.75rem 0',
    color: text,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    color: muted,
    marginBottom: '0.25rem',
  };

  const valueStyle: React.CSSProperties = {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: text,
  };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <h2 style={titleStyle}>VInvestAI</h2>
          <div style={{ fontSize: '0.875rem', color: muted, marginTop: '0.25rem' }}>
            Value investing valuation summary (educational). Focus: owner earnings, conservatism, margin of safety.
          </div>
          <div style={{ fontSize: '0.875rem', color: muted, marginTop: '0.5rem' }}>
            Symbol is controlled by the top search bar and stock chips.
          </div>
          {loading && (
            <div style={{ fontSize: '0.875rem', color: muted, marginTop: '0.5rem' }}>
              Analyzing {selectedStock}…
            </div>
          )}
        </div>
      </div>

      {error && (
        <div style={{ ...cardStyle, borderColor: '#ef4444', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 800, marginBottom: '0.25rem' }}>Could not compute valuation</div>
          <div style={{ color: muted, fontSize: '0.875rem' }}>{error}</div>
        </div>
      )}

      {result && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={cardStyle}>
              <div style={sectionTitleStyle}>Snapshot</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                <div>
                  <div style={labelStyle}>Symbol</div>
                  <div style={valueStyle}>{result.symbol}</div>
                </div>
                <div>
                  <div style={labelStyle}>Company</div>
                  <div style={valueStyle}>{result.companyName ?? '—'}</div>
                </div>
                <div>
                  <div style={labelStyle}>Price</div>
                  <div style={valueStyle}>{formatUSD(result.price)}</div>
                </div>
                <div>
                  <div style={labelStyle}>Market Cap</div>
                  <div style={valueStyle}>{formatCompactUSD(result.marketCap)}</div>
                </div>
                <div>
                  <div style={labelStyle}>Shares Outstanding</div>
                  <div style={valueStyle}>{result.sharesOutstanding ? result.sharesOutstanding.toLocaleString() : '—'}</div>
                  <div style={{ fontSize: '0.75rem', color: muted, marginTop: '0.25rem' }}>
                    Source: {result.sharesOutstandingSource}
                  </div>
                </div>
                <div>
                  <div style={labelStyle}>Base FCF (annual)</div>
                  <div style={valueStyle}>{formatCompactUSD(result.baseFCF)}</div>
                  <div style={{ fontSize: '0.75rem', color: muted, marginTop: '0.25rem' }}>Median of last up to 3 years</div>
                </div>
                <div>
                  <div style={labelStyle}>Owner Earnings (annual)</div>
                  <div style={valueStyle}>{formatCompactUSD(result.baseOwnerEarnings)}</div>
                  <div style={{ fontSize: '0.75rem', color: muted, marginTop: '0.25rem' }}>OCF − CapEx − SBC (median ≤3y)</div>
                </div>
                <div>
                  <div style={labelStyle}>FCF Yield</div>
                  <div style={valueStyle}>{formatPercent(result.fcfYield, 1)}</div>
                  <div style={{ fontSize: '0.75rem', color: muted, marginTop: '0.25rem' }}>FCF / Market Cap</div>
                </div>
              </div>
            </div>

            <div style={cardStyle}>
              <div style={sectionTitleStyle}>Quality Checklist</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                <div>
                  <div style={labelStyle}>FCF positive (last 5y)</div>
                  <div style={valueStyle}>{result.fcfPositiveYears5 === null ? '—' : `${result.fcfPositiveYears5}/5`}</div>
                </div>
                <div>
                  <div style={labelStyle}>Revenue CAGR (≈5y)</div>
                  <div style={valueStyle}>{formatPercent(result.revenueCagr5y, 1)}</div>
                </div>
                <div>
                  <div style={labelStyle}>Net Margin (latest)</div>
                  <div style={valueStyle}>{formatPercent(result.netMarginLatest, 1)}</div>
                </div>
                <div>
                  <div style={labelStyle}>Debt / FCF</div>
                  <div style={valueStyle}>
                    {result.debtToFCF === null || !Number.isFinite(result.debtToFCF) ? '—' : `${result.debtToFCF.toFixed(1)}x`}
                  </div>
                </div>
                <div>
                  <div style={labelStyle}>ROIC (latest)</div>
                  <div style={valueStyle}>{formatPercent(result.roicLatest, 1)}</div>
                </div>
                <div>
                  <div style={labelStyle}>ROE (latest)</div>
                  <div style={valueStyle}>{formatPercent(result.roeLatest, 1)}</div>
                </div>
                <div>
                  <div style={labelStyle}>Current Ratio (latest)</div>
                  <div style={valueStyle}>
                    {result.currentRatioLatest === null || !Number.isFinite(result.currentRatioLatest) ? '—' : result.currentRatioLatest.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div style={labelStyle}>Net Cash</div>
                  <div style={valueStyle}>{formatCompactUSD(result.netCash)}</div>
                </div>
              </div>
              <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: muted, lineHeight: 1.4 }}>
                Checklist is not a score; it’s a risk lens. Weakness here usually means bigger required margin-of-safety.
              </div>
            </div>

            <div style={cardStyle}>
              <div style={sectionTitleStyle}>DCF Assumptions (conservative)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                <div>
                  <div style={labelStyle}>Growth (years 1–10)</div>
                  <div style={valueStyle}>{formatPercent(result.growthRate)}</div>
                </div>
                <div>
                  <div style={labelStyle}>Discount Rate</div>
                  <div style={valueStyle}>{formatPercent(result.discountRate)}</div>
                </div>
                <div>
                  <div style={labelStyle}>Terminal Growth</div>
                  <div style={valueStyle}>{formatPercent(result.terminalGrowthRate)}</div>
                </div>
                <div>
                  <div style={labelStyle}>Projection Years</div>
                  <div style={valueStyle}>{result.projectionYears}</div>
                </div>
              </div>
              <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: muted, lineHeight: 1.4 }}>
                Buffett-style bias: prefer a margin-of-safety approach. When in doubt, assume less growth and demand a reasonable return.
              </div>
            </div>

            <div style={cardStyle}>
              <div style={sectionTitleStyle}>Valuation Output</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                <div>
                  <div style={labelStyle}>Composite Intrinsic / Share</div>
                  <div style={{ ...valueStyle, fontSize: '1.15rem' }}>{formatUSD(result.compositeIntrinsicValuePerShare, 2)}</div>
                  <div style={{ fontSize: '0.75rem', color: muted, marginTop: '0.25rem' }}>Median of available methods</div>
                </div>
                <div>
                  <div style={labelStyle}>Margin of Safety</div>
                  <div style={{ ...valueStyle, fontSize: '1.15rem' }}>{formatPercent(result.marginOfSafety, 1)}</div>
                  <div style={{ fontSize: '0.75rem', color: muted, marginTop: '0.25rem' }}>vs composite intrinsic</div>
                </div>
                <div>
                  <div style={labelStyle}>Buy / Sell Zones</div>
                  <div style={valueStyle}>
                    {formatUSD(result.buyBelow, 2)} – {formatUSD(result.sellAbove, 2)}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: muted, marginTop: '0.25rem' }}>30% MOS / 20% premium</div>
                </div>
              </div>

              <div style={{ height: '0.75rem' }} />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                <div>
                  <div style={labelStyle}>Entry for 12% / year (6y)</div>
                  <div style={{ ...valueStyle, fontSize: '1.15rem' }}>{formatUSD(result.entryPriceForTargetReturn, 2)}</div>
                  <div style={{ fontSize: '0.75rem', color: muted, marginTop: '0.25rem' }}>
                    Assumes price converges to composite intrinsic in {result.targetYears} years
                  </div>
                </div>
                <div>
                  <div style={labelStyle}>Implied return to intrinsic (6y)</div>
                  <div style={{ ...valueStyle, fontSize: '1.15rem' }}>{formatPercent(result.impliedAnnualReturnToIntrinsic, 1)}</div>
                  <div style={{ fontSize: '0.75rem', color: muted, marginTop: '0.25rem' }}>
                    Based on today price → composite intrinsic
                  </div>
                </div>
              </div>

              <div style={{ height: '0.75rem' }} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem' }}>
                {result.methods.map((m) => (
                  <div
                    key={m.name}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: '1rem',
                      padding: '0.5rem 0.75rem',
                      border: `1px solid ${border}`,
                      borderRadius: '0.5rem',
                      backgroundColor: isDarkMode ? '#111827' : '#ffffff',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 800, color: text }}>{m.name}</div>
                      {m.notes && <div style={{ fontSize: '0.75rem', color: muted }}>{m.notes}</div>}
                    </div>
                    <div style={{ fontWeight: 900, color: text }}>{formatUSD(m.perShare, 2)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={cardStyle}>
              <div style={sectionTitleStyle}>Decision Lens</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 800, marginBottom: '0.25rem' }}>
                {(() => {
                  const mos = result.marginOfSafety;
                  if (mos === null) return 'Insufficient data for a clean verdict';
                  if (mos >= 0.30) return 'Potential BUY (large margin of safety)';
                  if (mos >= 0.10) return 'WATCH (some margin of safety)';
                  if (mos > -0.10) return 'FAIR (close to intrinsic)';
                  return 'Potential SELL / Overvalued (negative margin of safety)';
                })()}
              </div>
              <div style={{ fontSize: '0.875rem', color: muted, lineHeight: 1.5 }}>
                This is not a forecast. Think in probabilities: quality first, price second.
              </div>

              <div style={{ height: '0.75rem' }} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                  <div style={{ color: muted, fontSize: '0.875rem' }}>Buy below (30% MOS)</div>
                  <div style={{ fontWeight: 800 }}>{formatUSD(result.buyBelow, 2)}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                  <div style={{ color: muted, fontSize: '0.875rem' }}>Sell above (20% premium)</div>
                  <div style={{ fontWeight: 800 }}>{formatUSD(result.sellAbove, 2)}</div>
                </div>
              </div>
            </div>

            <div style={cardStyle}>
              <div style={sectionTitleStyle}>Notes</div>
              <div style={{ fontSize: '0.875rem', color: muted, lineHeight: 1.55 }}>
                - Uses annual statements from the API.
                <br />- Combines multiple valuation lenses (DCF/EPV/Graham) to avoid over-reliance on one method.
                <br />- Base metrics use medians to reduce one-off noise.
                <br />- Growth is clipped to a conservative band.
                <br />- Always sanity-check: durability, capital allocation, competitive moat, dilution and cyclicality.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VInvestAIScreen;
